import { Alert, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppTFunction } from '@retail/app/i18n';
import { InputDatePicker } from '@retail/components';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { PricePerUnit } from '@retail/products/components';
import { formatPrice } from '@retail/products/utils';
import {
  calculatePercentChange,
  calculateSalesCoverageFromCostAndSalesPrice,
  getLLDate,
} from '@retail/utils';
import { ContainedButton, Dialog, TextButton, TextInputField } from '@shared/components';
import { ReactNode } from 'react';
import { useController, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { editSalesPriceSchema, EditSalesPriceSchema } from './editSalesPriceSchema';
import { ResultRow } from './ResultRow';

const PriceInfo = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={0.25} flex={1}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {children}
  </Stack>
);

interface Props {
  item: AssortmentPrices;
  includeVat: boolean;
  isSaving?: boolean;
  onSubmit: (values: EditSalesPriceSchema) => void;
  onClose: () => void;
}

export function EditSalesPriceDialog({ item, includeVat, isSaving, onSubmit, onClose }: Props) {
  const t = useAppTFunction();
  const {
    i18n: { language },
  } = useTranslation();

  const { salesPrice: currentSalesPrice, costPrice } = includeVat
    ? item.pricesIncVat
    : item.pricesExVat;

  const { control, handleSubmit, watch } = useForm<EditSalesPriceSchema>({
    resolver: yupResolver(editSalesPriceSchema(t)),
    mode: 'onChange',
    defaultValues: { fromDate: new Date() },
  });

  const salesPriceController = useController({ control, name: 'salesPrice' });
  const fromDateController = useController({ control, name: 'fromDate' });

  const newSalesPrice = Number((watch('salesPrice') + '').replace(',', '.'));
  const hasValidNewPrice = !!newSalesPrice && newSalesPrice > 0;

  const percentChange =
    hasValidNewPrice && currentSalesPrice
      ? calculatePercentChange(currentSalesPrice, newSalesPrice)
      : undefined;
  const newSalesCoverage =
    hasValidNewPrice && costPrice
      ? calculateSalesCoverageFromCostAndSalesPrice(costPrice, newSalesPrice)
      : undefined;

  const formatPercent = (value?: number) =>
    value !== undefined ? `${formatPrice(value, language)}%` : '-';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('myAssortment.actions.editSalesPrice')}</DialogTitle>

      <DialogContent>
        <Stack gap={3} pb={1}>
          <Stack>
            <Typography variant="subtitle1" fontWeight={600}>
              {item.primaryText}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`${item.mgItemNumber} · ${item.supplier.name}`}
            </Typography>
          </Stack>

          <Stack direction="row" gap={2} px={2} py={1.5} borderRadius={2} bgcolor="action.hover">
            <PriceInfo label={t('myAssortment.columns.currentSalesPrice')}>
              {currentSalesPrice ? (
                <PricePerUnit price={currentSalesPrice} unit={item.primaryUnit} />
              ) : (
                <Typography variant="subtitle2">-</Typography>
              )}
            </PriceInfo>
            <PriceInfo label={t('myAssortment.columns.validFrom')}>
              <Typography variant="subtitle2">{getLLDate(item.salesPriceDate) ?? '-'}</Typography>
            </PriceInfo>
            <PriceInfo label={t('myAssortment.columns.salesCoverage')}>
              <Typography variant="subtitle2">{formatPercent(item.coverageDegree)}</Typography>
            </PriceInfo>
          </Stack>

          {!costPrice && (
            <Alert severity="warning">
              <Typography variant="subtitle2">
                {t('myAssortment.productCard.salesPrice.error.noCostPrice')}
              </Typography>
              {t('myAssortment.status.noMarkupWithoutCostPrice')}
            </Alert>
          )}

          <Stack gap={2}>
            <Stack direction="row" gap={2} alignItems="flex-start">
              <TextInputField
                {...salesPriceController.field}
                value={salesPriceController.field.value ?? ''}
                error={salesPriceController.fieldState.error?.message}
                label={t('myAssortment.columns.newSalesPrice')}
                disabled={!costPrice}
                margin="none"
                size="small"
                fullWidth
                autoFocus
              />
              <InputDatePicker
                {...fromDateController.field}
                error={fromDateController.fieldState.error?.message}
                label={t('myAssortment.columns.validFrom')}
                disabled={!costPrice}
                minDate={new Date()}
                margin="none"
                size="small"
              />
            </Stack>
            <Stack gap={1}>
              <ResultRow label={t('myAssortment.columns.percentageChange')}>
                <Stack direction="row" alignItems="center" gap={0.5}>
                  {formatPercent(percentChange)}
                </Stack>
              </ResultRow>
              <ResultRow label={t('myAssortment.columns.newSalesCoverage')}>
                {formatPercent(newSalesCoverage)}
              </ResultRow>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <TextButton onClick={onClose}>{t('myAssortment.cancel')}</TextButton>
        <ContainedButton
          size="small"
          color="secondary"
          disabled={!costPrice || !hasValidNewPrice}
          loading={isSaving}
          onClick={handleSubmit(onSubmit)}
        >
          {t('myAssortment.actions.addToDrawer')}
        </ContainedButton>
      </DialogActions>
    </Dialog>
  );
}
